import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { X, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../ui/sheet";
import { publicApi } from "../../hooks/useApi";
import { useCart } from "../../context/CartContext";

export function CartDrawer({ open, onOpenChange }) {
  const { itemIds, removeItem } = useCart();
  const { data: catalog } = useQuery({ queryKey: ["books"], queryFn: async () => (await publicApi.get("/books")).data });

  const items = itemIds.map((id) => catalog?.find((b) => b.id === id)).filter(Boolean);
  const subtotal = items.reduce((sum, b) => sum + b.price, 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-black border-white/10 text-white w-full sm:w-96 flex flex-col" data-testid="cart-drawer">
        <SheetTitle className="text-sm uppercase tracking-wide text-white/70 mt-4 mb-6 font-normal">Your Cart</SheetTitle>
        <SheetDescription className="sr-only">Items currently in your Endure Beyond shopping cart</SheetDescription>
        {items.length === 0 ? (
          <p data-testid="cart-empty-message" className="text-white/70 text-sm">Your cart is empty.</p>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4">
            {items.map((b) => (
              <div key={b.id} data-testid={`cart-item-${b.slug}`} className="flex gap-3 items-center border-b border-white/10 pb-4">
                <img src={b.cover_image} alt={b.title} className="w-14 h-14 object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{b.title}</p>
                  <p className="text-xs text-white/70 mt-1">${b.price.toFixed(2)}</p>
                </div>
                <button data-testid={`cart-remove-${b.slug}`} onClick={() => removeItem(b.id)} aria-label={`Remove ${b.title}`} className="text-white/50 hover:text-white transition-colors duration-200 shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        {items.length > 0 && (
          <div className="border-t border-white/10 pt-4 mt-4">
            <div className="flex justify-between text-sm mb-4">
              <span className="text-white/70">Subtotal</span>
              <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <Link
              to="/checkout"
              onClick={() => onOpenChange(false)}
              data-testid="cart-checkout-button"
              className="flex items-center justify-center gap-2 bg-white text-black px-6 py-3 text-sm uppercase tracking-wide hover:bg-white/80 transition-colors duration-200"
            >
              <ShoppingBag className="w-4 h-4" /> Checkout
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function CartButton({ onClick, testId = "nav-cart-button" }) {
  const { itemIds } = useCart();
  return (
    <button data-testid={testId} onClick={onClick} aria-label="Open cart" className="relative text-white/80 hover:text-white transition-colors duration-200">
      <ShoppingBag className="w-5 h-5" />
      {itemIds.length > 0 && (
        <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 bg-white text-black text-[10px] leading-none w-4 h-4 rounded-full flex items-center justify-center font-medium">
          {itemIds.length}
        </span>
      )}
    </button>
  );
}
